import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Filtered searchable picker: type to narrow. */
export default function SearchableSelect({ id, label, required, value, onChange, options, placeholder, error }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || '');
  const wrapRef = useRef(null);

  useEffect(() => {
    let label = value || '';
    if (value && options && options.length) {
      const match = options.find((o) => (typeof o === 'object' ? o.value === value : o === value));
      if (match) label = typeof match === 'object' ? match.label : match;
    }
    setQ(label);
  }, [value, options]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((opt) => {
      const labelStr = typeof opt === 'object' ? opt.label : opt;
      return labelStr.toLowerCase().includes(needle);
    });
  }, [q, options]);

  const pick = useCallback(
    (opt) => {
      const val = typeof opt === 'object' ? opt.value : opt;
      const label = typeof opt === 'object' ? opt.label : opt;
      onChange(val);
      setQ(label);
      setOpen(false);
    },
    [onChange],
  );

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const showError = !!error;

  return (
    <div className={`field ${showError ? 'field-has-error' : ''}`} ref={wrapRef}>
      {label && (
        <label htmlFor={id} style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>
          {label}
          {required ? ' *' : ''}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          name={`search-${id}`}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          required={required}
          value={q}
          onChange={(e) => {
            const v = e.target.value;
            setQ(v);
            // If the user clears the input, clear the selection
            if (!v) onChange('');
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || "Type to search..."}
          aria-expanded={open}
          aria-autocomplete="list"
          style={{
            borderRadius: '12px',
            height: '3.25rem',
            ...(showError ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(220,38,38,0.12)' } : undefined)
          }}
        />
        {open && filtered.length > 0 && (
          <ul
            role="listbox"
            className="country-combobox-list"
            style={{ width: '100%', maxHeight: '250px', overflowY: 'auto', zIndex: 1000 }}
          >
            {filtered.map((opt, i) => {
              const label = typeof opt === 'object' ? opt.label : opt;
              const val = typeof opt === 'object' ? opt.value : opt;
              return (
                <li key={val || i}>
                  <button 
                    type="button" 
                    role="option" 
                    className="country-combobox-opt" 
                    onMouseDown={(e) => e.preventDefault()} 
                    onClick={() => pick(opt)}
                  >
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {error && <span className="field-error-msg">{error}</span>}
    </div>
  );
}
